import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Session, SessionEndReason, SessionPersistence } from './types';

export interface SessionState {
  current: Session | null;
  endReason: SessionEndReason | null;
}

const initialState: SessionState = {
  current: null,
  endReason: null,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /** A user signed in (or registered) in this tab. */
    sessionStarted(
      state,
      action: PayloadAction<{ session: Session; persistence: SessionPersistence }>,
    ) {
      state.current = action.payload.session;
      state.endReason = null;
    },
    /** A previously persisted session was loaded (app start-up or another tab signed in). */
    sessionRestored(state, action: PayloadAction<Session>) {
      state.current = action.payload;
      state.endReason = null;
    },
    signedOut(state) {
      state.current = null;
      state.endReason = 'signedOut';
    },
    /** The token expired or the API rejected it (401). */
    sessionExpired(state) {
      if (state.current === null) return;
      state.current = null;
      state.endReason = 'expired';
    },
  },
  selectors: {
    selectSession: (state) => state.current,
    selectIsAuthenticated: (state) => state.current !== null,
    selectCurrentUser: (state) => state.current?.user ?? null,
    selectAccessToken: (state) => state.current?.accessToken ?? null,
    selectSessionEndReason: (state) => state.endReason,
  },
});

export const { sessionStarted, sessionRestored, signedOut, sessionExpired } = sessionSlice.actions;

export const {
  selectSession,
  selectIsAuthenticated,
  selectCurrentUser,
  selectAccessToken,
  selectSessionEndReason,
} = sessionSlice.selectors;

/** Minimal state shape the selectors need — avoids importing the app's RootState into shared code. */
export type StateWithSession = Record<typeof sessionSlice.reducerPath, SessionState>;
